/* import libraries */
import { useState, useEffect } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'

/* import components */
import Container from 'react-bootstrap/Container'
import Menu from '../components/Menu'
import ArticleInfo from '../components/ArticleInfo'
import HelpFormInfo from '../components/HelpFormInfo'
import TripCard from '../components/TripCard'
import Loading from './Loading'
import Error from './Error'
// import ExternalWebsite from '../components/ExternalWebsite'

/* import services */
import tripsApi from '../api/tripsApi'
import { saveToken } from '../utils/utils'
import { getArticle, getTrip } from '../controller/controller'
import { formsWithNoTripId, feeWaiverArticleId } from '../utils/constants'

const ArticleHelpForm = () => {
  const { state } = useLocation()
  const { sectionId, articleId, tripId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loadError, setLoadError] = useState(false)
  const [noTripsError, setNoTripsError] = useState(false)
  const [page, setPage] = useState('info')
  const [loading, setLoading] = useState(true)
  const [articleData, setArticleData] = useState()
  const [trip, setTrip] = useState()
  const [showTripOption, setShowTripOption] = useState(false)
  // const [insuranceUrl, setInsuranceUrl] = useState('')
  const [tripsData, setTripsData] = useState([])
  const [currPage, setCurrPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [bgColor, setBgColor] = useState('#ffffff')

  const handleChangeTab = (e) => {
    e.target.outerText === 'Info' ? setPage('info') : setPage('help-form')
  }

  const getAllTrips = async (page) => {
    try {
      const response = await tripsApi.getTripsWithin30Days({
        perPage: 10,
        page: page,
      })

      if (response.data.count === 0 && currPage === 1) {
        setNoTripsError(true)
      }
      if (response.status) {
        setCurrPage(currPage + 1)
        setTripsData([...tripsData, ...response.data.data])
        if (response.data.data.length < 10) {
          setHasMore(false)
        }
      } else {
        console.error(response.error.msg)
      }
    } catch (error) {
      setLoadError(true)
      console.error('Error while fetching trips: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectTrip = async (trip) => {
    if (feeWaiverArticleId.hasOwnProperty(articleId)) {
      await getArticle(articleId, setArticleData, setLoadError, setLoading, trip.id)
    }
    await getTrip(trip.id, setTrip, setLoadError, setLoading, articleData, setArticleData)
    setLoading(false)
    setShowTripOption(false)
  }

  const handleFeeWaiverArticle = () => {
    setShowTripOption(true)
    if (tripsData.length === 0) getAllTrips(currPage)
  }

  useEffect(() => {
    // Save the token if opened from a mobile entry point
    if (searchParams.get('token')) {
      saveToken(searchParams.get('token'), setLoading)
    }

    /**
         * COMMENTED AS OF NOW
        if (articleId < 0 && searchParams.get('insuranceUrl')) {
            setInsuranceUrl(searchParams.get('insuranceUrl'))
            setLoadError(false)
            setLoading(false)
            return
        }
         */

    const tripIdForArticle = trip?.id || tripId

    // Fee waiver article handling
    if (feeWaiverArticleId.hasOwnProperty(articleId) && !tripIdForArticle) {
      return handleFeeWaiverArticle()
    } else {
      // Fetch article contents
      if (!articleData) {
        getArticle(articleId, setArticleData, setLoadError, setLoading, tripIdForArticle)
      }

      // Check if Article has a form where Trip ID is required
      if (articleData?.formData && !trip?.id && !formsWithNoTripId.hasOwnProperty(articleData?.id)) {
        setShowTripOption(true)
        if (tripsData.length === 0) {
          getAllTrips(currPage)
        }
      }

      // If from mobile trip details entry point, get trip details
      if (!trip?.id && tripId && articleId) {
        getTrip(tripId, setTrip, setLoadError, setLoading)
      }
    }
  }, [trip, articleData])

  if (loading) return <Loading />

  if (loadError) return <Error />

  /**
     * COMMENTED AS OF NOW
    if (insuranceUrl) {
      return (
        <>
          <Menu title='RydeHELP' bgColor={'#ffffff'} />
          <ExternalWebsite src={`${searchParams.get('insuranceUrl')}`} title='RydeHELP' />
        </>
      )
    }
     */

  return (
    <>
      <Menu title='RydeHELP' bgColor={trip || tripId ? bgColor : '#ffffff'} />

      {/* If form requires a tripId, user to select which trip he/she has an issue with */}
      {(articleData?.formData && showTripOption) ||
      (feeWaiverArticleId.hasOwnProperty(articleId) &&
        showTripOption &&
        !(trip?.id || tripId)) ? (
        <>
          <Container style={{ paddingTop: '15px' }}>
            <div className='grey font-bold' style={{ fontSize: '16px' }}>
              Which Trip?
            </div>
            <div className='grey' style={{ fontSize: '14px' }}>
              Select the trip you have had an issue with from the past 30 days.
            </div>
            {noTripsError ? (
              <div
                className='grey font-bold'
                style={{ fontSize: '16px', paddingTop: '15px' }}
              >
                You have no trips from the past 30 days!
              </div>
            ) : (
              <InfiniteScroll
                dataLength={currPage * 10}
                next={() => getAllTrips(currPage)}
                hasMore={hasMore}
                loader={<h6>Loading...</h6>}
              >
                {tripsData.map((trip, i) => (
                  <TripCard
                    key={i}
                    trip={trip}
                    onClick={() => handleSelectTrip(trip)}
                  />
                ))}
              </InfiniteScroll>
            )}
          </Container>
        </>
      ) : (
        <Container style={{ paddingTop: '10px', backgroundColor: bgColor }}>
          {articleData?.formData ? (
            <div
              style={{
                display: 'flex',
                textAlign: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                className={
                  bgColor !== '#ffffff'
                    ? 'grey font-bold'
                    : page === 'info'
                    ? 'grey font-bold underline'
                    : 'inactive font-bold'
                }
                onClick={handleChangeTab}
                style={{ cursor: 'pointer' }}
              >
                Info
              </div>
              <div style={{ padding: '0 20px' }} />
              <div
                className={
                  page === 'help-form'
                    ? 'grey font-bold underline'
                    : 'inactive font-bold'
                }
                onClick={handleChangeTab}
                style={{ cursor: 'pointer' }}
              >
                Help Form
              </div>
            </div>
          ) : (
            <></>
          )}

          {!showTripOption ? (
            <>
              {page === 'info' ? (
                <ArticleInfo
                  setPage={setPage}
                  setBgColor={setBgColor}
                  topic={state ? state.topic : articleData}
                  trip={state && state.trip ? state.trip : trip}
                  article={articleData}
                  loading={loading}
                />
              ) : (
                <HelpFormInfo
                  trip={state && state.trip ? state.trip : trip}
                  article={articleData}
                  formData={articleData?.formData}
                />
              )}
            </>
          ) : (
            <></>
          )}
        </Container>
      )}
    </>
  )
}

export default ArticleHelpForm
